/**
 * Turning reminders on (#1332, #1333, #1334).
 *
 * ⛔ THE ORDER OF THE STEPS IS THE DESIGN. On iOS a push is impossible until
 * the app is on the Home Screen, so asking for notification permission first
 * would produce a refusal the browser then remembers. Install, then permission,
 * then the switch.
 *
 * ⚠️ And the honest part: on iPhone there is no install API. Safari fires no
 * event and offers no button, so the only thing to do is say what to tap.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { twoPlates } from "../api/twoPlates";
import { canPromptInstall, promptInstall, isStandalone, isIOS } from "../lib/install";

const KIND_COPY: { key: "slowCooker" | "shopDay" | "planEnding" | "cookLog"; name: string; note: string }[] = [
  {
    key: "slowCooker",
    name: "Start the slow cooker",
    note: "The morning a slow cooker is on the plan. Miss it and dinner does not happen.",
  },
  {
    key: "shopDay",
    name: "Shopping day",
    note: "Only when nothing is planned yet. Never when you have already done it.",
  },
  { key: "planEnding", name: "The plan runs out", note: "On the last day, so the next three days are not a surprise." },
  { key: "cookLog", name: "How was dinner", note: "The morning after, one tap each. Changes the deck, not anybody's answers." },
];

export function Reminders() {
  const queryClient = useQueryClient();
  const { data } = useQuery({ queryKey: ["reminders"], queryFn: twoPlates.reminders });
  const [installed, setInstalled] = useState(isStandalone());
  const [permission, setPermission] = useState<NotificationPermission | "unsupported">(
    typeof Notification === "undefined" ? "unsupported" : Notification.permission,
  );
  const [said, setSaid] = useState<string | null>(null);

  useEffect(() => {
    const t = setInterval(() => setInstalled(isStandalone()), 2000);
    return () => clearInterval(t);
  }, []);

  const save = useMutation({
    mutationFn: twoPlates.updateReminders,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["reminders"] }),
  });

  const test = useMutation({
    mutationFn: twoPlates.testReminder,
    onSuccess: (r) =>
      setSaid(
        r.sent > 0
          // ⚠️ "Handed over" is the honest word. A push service returning 201
          // means it ACCEPTED the message, not that a phone displayed it — and
          // conflating those sends somebody hunting for a server bug that is
          // really a Focus mode.
          ? `Handed to ${r.sent === 1 ? "your device" : r.sent + " devices"}. If nothing appears, see the setup steps below.`
          : `Nothing went out (${r.skipped ?? "no devices registered"}).`,
      ),
  });

  if (!data) return null;

  if (!data.configured) {
    return (
      <section className="border-line rounded-xl border p-5">
        <h2 className="font-semibold">Reminders</h2>
        <p className="text-muted mt-2 text-sm">Not switched on for this server yet.</p>
      </section>
    );
  }

  const needsInstallFirst = isIOS() && !installed;

  async function enable() {
    // 1. iOS: nothing can happen until it is on the Home Screen.
    if (needsInstallFirst) return;

    // 2. Permission, from inside this tap.
    if (permission !== "granted") {
      const result = await Notification.requestPermission();
      setPermission(result);
      if (result !== "granted") {
        setSaid("Your browser said no. You can change that in its site settings.");
        return;
      }
    }

    // 3. Subscribe this device, then turn the household switch on.
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: data!.publicKey!,
    });
    const json = sub.toJSON() as { endpoint: string; keys: { p256dh: string; auth: string } };
    await twoPlates.subscribePush({ endpoint: json.endpoint, keys: json.keys });
    save.mutate({ on: true });
  }

  return (
    <section className="border-line rounded-xl border p-5">
      <h2 className="font-semibold">Reminders</h2>

      {needsInstallFirst && (
        <div className="mt-3 rounded-lg border p-3" style={{ borderColor: "var(--color-plate-a)" }}>
          <p className="text-plate-a text-xs uppercase tracking-wide">One step first, on iPhone</p>
          <p className="text-muted mt-1.5 text-sm">
            Tap the Share button, then <strong>Add to Home Screen</strong>, and open
            Two Plates from there. Apple only allows notifications to apps added
            that way — there is no button we can give you for it.
          </p>
        </div>
      )}

      {!needsInstallFirst && canPromptInstall() && !installed && (
        <button
          onClick={() => promptInstall()}
          className="border-line mt-3 w-full rounded-lg border py-3 text-sm font-medium"
        >
          Add to your home screen
        </button>
      )}

      {!data.on ? (
        <>
          <p className="text-muted mt-3 text-sm">
            One reminder matters more than the rest: a slow cooker has to be
            started in the morning, and being told at six o&rsquo;clock is being
            told too late.
          </p>
          <button
            onClick={() => void enable()}
            disabled={needsInstallFirst || save.isPending}
            className="bg-plate-b text-ground tap mt-4 w-full rounded-lg font-semibold disabled:opacity-40"
          >
            {save.isPending ? "Turning on…" : "Turn reminders on"}
          </button>
          <p className="text-muted mt-2 text-xs">
            Nothing between nine at night and half past six in the morning, ever.
          </p>
        </>
      ) : (
        <>
          <p className="text-muted mt-2 text-xs">
            On, for {data.devices} {data.devices === 1 ? "device" : "devices"}. Quiet from 9pm to 6:30am.
          </p>

          <ul className="mt-4 flex flex-col gap-2.5">
            {KIND_COPY.map((k) => (
              <li key={k.key} className="border-line rounded-lg border p-3">
                <label className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={data.kinds[k.key]}
                    onChange={(e) => save.mutate({ [k.key]: e.target.checked })}
                    className="mt-1"
                  />
                  <span>
                    <span className="block text-sm font-medium">{k.name}</span>
                    <span className="text-muted block text-xs">{k.note}</span>
                  </span>
                </label>
              </li>
            ))}
          </ul>

          <div className="mt-4 flex items-center gap-4">
            <button
              onClick={() => test.mutate()}
              disabled={test.isPending}
              className="border-line rounded-lg border px-3 py-2 text-xs font-medium disabled:opacity-50"
            >
              {test.isPending ? "Sending…" : "Send me one now"}
            </button>
            <button
              onClick={() => save.mutate({ on: false })}
              className="text-muted text-sm underline underline-offset-4"
            >
              Turn them all off
            </button>
          </div>
        </>
      )}

      {said && <p className="text-plate-b mt-3 text-sm">{said}</p>}
    </section>
  );
}
